import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Award, Download, ArrowLeft, Share2, ExternalLink, Loader2, Linkedin, Twitter, Copy } from "lucide-react";
import html2pdf from "html2pdf.js";
import { useT } from "@/lib/i18n";

export default function CourseCertificate() {
  const { certId } = useParams();
  const { t, lang } = useT();
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    api.get(`/academy/certificates/${certId}`)
      .then((r) => setCert(r.data))
      .catch(() => toast.error("Certificat introuvable"))
      .finally(() => setBusy(false));
  }, [certId]);

  const verifyUrl = `${window.location.origin}/verify/${certId}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(verifyUrl)}&margin=0&color=1F3A5F&bgcolor=FFFFFF`;

  const downloadPdf = () => {
    if (!printRef.current) return;
    const opt = {
      margin: 0,
      filename: `geoVINUM-Certificat-${cert.course_title.replace(/\s+/g, "-")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
    };
    html2pdf().set(opt).from(printRef.current).save();
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Certificat geoVINUM · ${cert.course_title}`,
          text: `Je viens de décrocher ma certification "${cert.course_title}" sur geoVINUM Academy !`,
          url: verifyUrl,
        });
      } else {
        await navigator.clipboard.writeText(verifyUrl);
        toast.success("Lien de vérification copié !");
      }
    } catch (_) { /* user cancelled */ }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Impossible de copier"); }
  };

  const shareLinkedInPost = () => {
    // Share as a LinkedIn feed post
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`;
    window.open(url, "_blank", "width=680,height=520");
  };

  const addToLinkedInProfile = () => {
    // LinkedIn "Add to Profile" — Certifications section deep-link
    // Docs: https://learn.microsoft.com/en-us/linkedin/consumer/integrations/self-serve/adding-certifications
    if (!cert) return;
    const issued = new Date(cert.issued_at || Date.now());
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: `${cert.course_title} — geoVINUM Academy`,
      organizationName: "geoVINUM",
      issueYear: String(issued.getFullYear()),
      issueMonth: String(issued.getMonth() + 1),
      certUrl: verifyUrl,
      certId: cert.id,
    });
    window.open(`https://www.linkedin.com/profile/add?${params.toString()}`, "_blank");
  };

  const shareTwitter = () => {
    const text = `Je viens de décrocher ma certification ${cert.course_title} sur @geoVINUM Academy 🍷`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(verifyUrl)}`;
    window.open(url, "_blank", "width=680,height=420");
  };

  if (busy) return <div className="max-w-4xl mx-auto px-5 py-24 text-[#8B95A1]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…</div>;
  if (!cert) return <div className="max-w-4xl mx-auto px-5 py-24 text-[#8B95A1]">Certificat introuvable.</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="certificate-page">
      <Link to="/courses" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#E5276A]">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour aux parcours
      </Link>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap items-center gap-2 justify-end">
        <button onClick={downloadPdf} data-testid="cert-download"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
          <Download className="w-4 h-4" /> Télécharger PDF
        </button>
        <button
          onClick={addToLinkedInProfile}
          data-testid="cert-linkedin-profile"
          title="Ajouter à mon profil LinkedIn (Certifications)"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0A66C2] text-white text-sm font-bold hover:bg-[#004182]"
        >
          <Linkedin className="w-4 h-4" /> Ajouter à LinkedIn
        </button>
        <button
          onClick={shareLinkedInPost}
          data-testid="cert-linkedin-post"
          title="Partager sur mon fil LinkedIn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#0A66C2]/40 text-sm font-bold text-[#0A66C2] hover:bg-[#0A66C2]/8"
        >
          <Linkedin className="w-4 h-4" /> Post LinkedIn
        </button>
        <button
          onClick={shareTwitter}
          data-testid="cert-twitter"
          title="Partager sur X (Twitter)"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]"
        >
          <Twitter className="w-4 h-4" /> X
        </button>
        <button onClick={share} data-testid="cert-share"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
          <Share2 className="w-4 h-4" /> Partager
        </button>
        <button onClick={copyLink} data-testid="cert-copy"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
          <Copy className="w-4 h-4" /> {copied ? "Copié !" : "Copier l'URL"}
        </button>
        <a href={verifyUrl} target="_blank" rel="noreferrer" data-testid="cert-verify-link"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
          <ExternalLink className="w-4 h-4" /> Vérifier
        </a>
      </div>

      {/* Copy hint bar */}
      <div className="mt-3 rounded-xl bg-[#FBF7F3] p-3 flex items-center gap-2 flex-wrap text-xs">
        <span className="text-[#8B95A1] uppercase tracking-widest font-bold text-[10px]">URL publique</span>
        <code className="text-[#1F3A5F] break-all">{verifyUrl}</code>
      </div>

      {/* Printable certificate */}
      <div ref={printRef} className="mt-5 mx-auto bg-white shadow-xl relative overflow-hidden"
        style={{
          width: "297mm",
          maxWidth: "100%",
          aspectRatio: "297/210",
          border: "12px solid #1F3A5F",
          boxSizing: "border-box",
          padding: "16mm 22mm",
          background: "linear-gradient(135deg, #FFFFFF 0%, #FBF7F3 100%)",
          fontFamily: "'Lato', system-ui, sans-serif",
        }}
      >
        {/* Corner ornaments */}
        <div style={{ position: "absolute", top: 20, right: 20, width: 40, height: 40, background: "#E5276A" }} />
        <div style={{ position: "absolute", bottom: 20, left: 20, width: 40, height: 40, background: "#E5276A" }} />

        <div style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between", alignItems: "center", textAlign: "center", position: "relative", zIndex: 2 }}>
          {/* Header — logo + label */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <img
              src={`${window.location.origin}/brand/logo-color.png`}
              alt="geoVINUM"
              style={{ height: 40, width: "auto" }}
              crossOrigin="anonymous"
            />
            <div style={{ fontFamily: "'Expletus Sans', 'Lato', serif", fontSize: 12, letterSpacing: 6, color: "#E5276A", fontWeight: 700, textTransform: "uppercase" }}>
              {t("cert.header")}
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, letterSpacing: 3, color: "#8B95A1", textTransform: "uppercase" }}>
              {t("cert.subheader").replace("{level}", cert.course_level_label || "")}
            </div>
          </div>

          {/* Middle — body */}
          <div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 14, color: "#4A5568", marginBottom: 4 }}>{t("cert.certifies")}</div>
            <div style={{ fontFamily: "'Expletus Sans', 'Lato', serif", fontSize: 42, color: "#1F3A5F", fontWeight: 700, lineHeight: 1.1 }}>
              {cert.user_name || "Œnophile"}
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 14, color: "#4A5568", marginTop: 12 }}>{t("cert.completed")}</div>
            <div style={{ fontFamily: "'Expletus Sans', 'Lato', serif", fontSize: 30, color: "#E5276A", fontWeight: 700, marginTop: 4 }}>
              {cert.course_title}
            </div>
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 12, color: "#8B95A1", marginTop: 8, letterSpacing: 2, textTransform: "uppercase" }}>
              {t("cert.scoreRegion").replace("{score}", cert.final_score).replace("{region}", cert.region)}
            </div>
          </div>

          {/* Footer — date, QR, signature */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", width: "100%", gap: 20 }}>
            <div style={{ textAlign: "left", fontFamily: "'Lato', sans-serif" }}>
              <div style={{ fontSize: 11, color: "#8B95A1", letterSpacing: 2, textTransform: "uppercase" }}>{t("cert.issuedOn")}</div>
              <div style={{ fontSize: 14, color: "#1F3A5F", fontWeight: 700 }}>{formatDate(cert.issued_at, lang)}</div>
              <div style={{ fontSize: 10, color: "#8B95A1", marginTop: 4 }}>ID · {cert.id}</div>
            </div>
            <div style={{ textAlign: "center", fontFamily: "'Lato', sans-serif" }}>
              <img src={qrUrl} alt="QR code" style={{ width: 80, height: 80 }} crossOrigin="anonymous" />
              <div style={{ fontSize: 9, color: "#8B95A1", marginTop: 4, letterSpacing: 1 }}>{t("cert.verifyAuth")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'Allison', 'Great Vibes', cursive", fontSize: 56, color: "#1F3A5F", lineHeight: 1, marginBottom: 2 }}>
                Audrey Sousa Chaillet
              </div>
              <div style={{ height: 1, width: 200, background: "#1F3A5F", marginLeft: "auto", marginTop: 2, marginBottom: 4 }} />
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: 10, color: "#8B95A1", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>
                {t("cert.founderTitle")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-[#8B95A1] mt-6 text-center max-w-2xl mx-auto">
        {t("cert.footer")}
      </p>
    </div>
  );
}

function formatDate(iso, lang = "fr") {
  const localeMap = { fr: "fr-FR", en: "en-US", es: "es-ES", pt: "pt-PT" };
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(localeMap[lang] || "fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch (_) { return iso?.slice(0, 10) || ""; }
}
